import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Facebook, Twitter, Instagram, ChevronDown, ChevronRight, Mail, Phone, Megaphone, ArrowLeft } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenSubmenu, setMobileOpenSubmenu] = useState(null);
  const timeoutRef = useRef(null);
  const mobileOpenSubmenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 50);

      if (currentScrollY <= 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [lastScrollY]);

  const handleMouseEnter = (itemName) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpenDropdown(itemName);
  };

  const handleMouseLeave = () => {
    // Add delay before closing menu
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 800); // 800ms delay - increased for better UX
  };

  const menuData = [
    {
      name: 'BAŞKAN',
      path: '/baskan'
    },
    {
      name: 'KURUMSAL',
      path: '/kurumsal',
      submenu: [
        { name: 'Hakkımızda', path: '/kurumsal' },
        { name: 'Meclis Üyeleri', path: '/meclis-uyeleri' },
        { name: 'Mevzuat', path: '/mevzuat' },
        { name: 'Faaliyet Raporları', path: '/faaliyet-raporlari' },
        { name: 'İstatistiksel Bilgiler', path: '/istatistikler' }
      ]
    },
    {
      name: 'HİZMETLERİMİZ',
      path: '/hizmetler',
      submenu: [
        { name: 'Çevre Hizmetleri', path: '/hizmetler' },
        { name: 'Ulaşım', path: '/hizmetler' },
        { name: 'İmar ve Şehircilik', path: '/hizmetler' },
        { name: 'Sosyal Hizmetler', path: '/hizmetler' },
        { name: 'Park ve Bahçeler', path: '/hizmetler' }
      ]
    },
    {
      name: 'HİZMET REHBERİ',
      path: '/hizmet-rehberi',
      mobileOnly: true
    },
    {
      name: 'BİRİMLER',
      path: '/birimler',
      submenu: [
        { name: 'Fen İşleri', path: '/birimler' },
        { name: 'İmar Müdürlüğü', path: '/birimler' },
        { name: 'Zabıta', path: '/birimler' },
        { name: 'Kültür İşleri', path: '/birimler' }
      ]
    },
    {
      name: 'PROJELER',
      path: '/projeler',
      submenu: [
        { name: 'Tamamlanan Projeler', path: '/projeler' },
        { name: 'Devam Eden Projeler', path: '/projeler' },
        { name: 'Planlanan Projeler', path: '/projeler' }
      ]
    },
    {
      name: 'KENT REHBERİ',
      path: '/kent-rehberi'
    },
    {
      name: 'HABERLER',
      path: '/haberler',
      submenu: [
        { name: 'Tüm Haberler', path: '/haberler' },
        { name: 'Duyurular', path: '/duyurular' },
        { name: 'Etkinlikler', path: '/etkinlikler' },
        { name: 'İhaleler', path: '/ihaleler' }
      ]
    },
    {
      name: 'İLETİŞİM',
      path: '/iletisim',
      submenu: [
        { name: 'İletişim Formu', path: '/iletisim' },
        { name: 'Bilgi Edinme Hakkı', path: '/bilgi-edinme' },
        { name: 'Şikayet ve Geri Bildirim', path: '/sikayet-ve-geri-bildirim' },
        { name: 'Sıkça Sorulan Sorular', path: '/sss' },
        { name: 'Yardım', path: '/yardim' }
      ]
    }
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 transform ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      } ${
        isScrolled || !isHomePage ? 'bg-gradient-to-r from-[#2171b5] to-[#1e5a8e] shadow-lg' : 'bg-transparent'
      }`}>
        {/* Mobile Header - Blue Bar */}
        <div className="lg:hidden bg-gradient-to-r from-[#2171b5] to-[#1e5a8e] w-full">
          <div className="relative flex items-center h-14 px-4">
            <div className="flex items-center gap-3 z-50 relative">
              {!isHomePage && (
                <button
                  onClick={() => navigate(-1)}
                  aria-label="Geri"
                  className="text-white flex items-center gap-2 font-semibold"
                >
                  <ArrowLeft size={24} aria-hidden="true" />
                </button>
              )}
              {/* Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
                className="text-white flex items-center gap-2 font-semibold"
              >
                <Menu size={24} aria-hidden="true" />
                <span>MENU</span>
              </button>
            </div>

            {/* Centered Logo Badge — always shown with a white backing on mobile,
                regardless of scroll position, so it stays legible over the hero video */}
            <Link
              to="/"
              className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b-[2rem] p-3 flex items-center justify-center z-10 bg-white shadow-xl ring-1 ring-black/5"
            >
              <img
                src="/logo.png?v=4"
                alt="Erimli Belediyesi Logo"
                width={120}
                height={120}
                decoding="async"
                className="w-20 h-20 sm:w-24 sm:h-24 object-contain filter drop-shadow-sm"
              />
            </Link>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Left Menu */}
            <nav className="hidden 2xl:flex items-center gap-6 flex-1">
              {!isHomePage && (
                <button
                  onClick={() => navigate(-1)}
                  className="text-white font-semibold hover:text-white/80 transition flex items-center gap-2"
                >
                  <ArrowLeft size={20} />
                  <span>GERİ</span>
                </button>
              )}
              {menuData.filter(item => !item.mobileOnly).slice(0, 4).map((item) => (
                <div
                  key={item.name}
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={item.path}
                    className="text-white font-semibold hover:text-white/80 transition text-sm tracking-wide flex items-center gap-1"
                  >
                    {item.name}
                    {item.submenu && <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.submenu && openDropdown === item.name && (
                    <div className="absolute top-full left-[30%] mt-2 w-56 bg-white rounded-lg shadow-xl py-2 animate-slide-right z-50">
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          to={subitem.path}
                          className="block px-4 py-2 text-gray-700 hover:bg-[#2171b5] hover:text-white transition text-sm"
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Logo - Center with Animation */}
            <Link
              to="/"
              className={`absolute left-1/2 top-0 -translate-x-1/2 rounded-b-[2.5rem] px-4 pb-3 pt-2 flex items-center justify-center z-10 animate-slide-down transition-all duration-500 ${
                isScrolled || !isHomePage
                  ? 'bg-white shadow-xl ring-1 ring-black/5 border-b border-x border-gray-100'
                  : 'bg-transparent shadow-none border-transparent'
              }`}
            >
              <img
                src="/logo.png?v=4"
                alt="Erimli Belediyesi Logo"
                width={144}
                height={144}
                decoding="async"
                className="w-28 h-28 lg:w-32 lg:h-32 object-contain filter drop-shadow-sm transition-transform duration-300 hover:scale-105"
              />
            </Link>

            {/* Right Menu */}
            <nav className="hidden 2xl:flex items-center gap-6 flex-1 justify-end">
              {menuData.filter(item => !item.mobileOnly).slice(4).map((item) => (
                <div
                  key={item.name}
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={item.path}
                    className="text-white font-semibold hover:text-white/80 transition text-sm tracking-wide flex items-center gap-1"
                  >
                    {item.name}
                    {item.submenu && <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />}
                  </Link>

                  {/* Dropdown Menu */}
                  {item.submenu && openDropdown === item.name && (
                    <div className={`absolute top-full mt-2 w-56 bg-white rounded-lg shadow-xl py-2 animate-slide-right z-50 ${item.name === 'PROJELER' ? 'left-0' : 'right-0'}`}>
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          to={subitem.path}
                          className="block px-4 py-2 text-gray-700 hover:bg-[#2171b5] hover:text-white transition text-sm"
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Social Icons */}
            <div className="hidden 2xl:flex items-center gap-2 ml-4">
              <button onClick={() => navigate('/ara')} aria-label="Ara" className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition text-white">
                <Search size={16} aria-hidden="true" />
              </button>
              <a href="https://www.facebook.com/erimlibelediye/?locale=tr_TR" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition text-white">
                <Facebook size={16} aria-hidden="true" />
              </a>
              <a href="https://x.com/erimlibelediyesi" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition text-white">
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden="true">
                  <path d="M18.901 3H22.5L14.477 12.026L23.75 24H16.526L10.743 16.596L4.125 24H0.526L9.049 14.373L0.053 3H7.476L12.756 9.946L18.901 3ZM17.653 21.829H19.645L6.426 5.021H4.29L17.653 21.829Z" />
                </svg>
              </a>
              <a href="https://www.instagram.com/erimli.belediyesi/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition text-white">
                <Instagram size={16} aria-hidden="true" />
              </a>
            </div>

          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {
        isMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 z-[105] bg-black/50 transition-opacity"
            onClick={() => setIsMenuOpen(false)}
          >
            {/* Mobile Menu Sidebar */}
            <div
              className="fixed left-0 top-0 bottom-0 w-2/3 max-w-sm bg-gradient-to-r from-[#2171b5] to-[#1e5a8e] shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto text-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/20">
                <Link
                  to="/"
                  className="text-white font-bold"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Ana Sayfa
                </Link>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Menüyü kapat"
                  className="text-white hover:text-white/80 transition p-2"
                >
                  <X size={24} aria-hidden="true" />
                </button>
              </div>

              {/* Menu Title */}
              <div className="px-4 py-3 border-b border-white/20">
                <h2 className="text-white/90 font-bold text-sm uppercase">MENU</h2>
              </div>

              {/* Menu Items */}
              <nav className="py-2">
                {menuData.map((item) => (
                  <div key={item.name}>
                    <div className="flex items-center justify-between px-4 py-3 hover:bg-white/10 transition">
                      <Link
                        to={item.path}
                        className="flex-1 text-white font-semibold text-sm"
                        onClick={() => !item.submenu && setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                      {item.submenu && (
                        <button
                          onClick={() => setMobileOpenSubmenu(mobileOpenSubmenu === item.name ? null : item.name)}
                          aria-label={`${item.name} alt menüsünü ${mobileOpenSubmenu === item.name ? 'kapat' : 'aç'}`}
                          className="text-white/70 p-1"
                        >
                          <ChevronRight size={18} aria-hidden="true" className={`transition-transform ${mobileOpenSubmenu === item.name ? 'rotate-90' : ''
                            }`} />
                        </button>
                      )}
                    </div>

                    {/* Mobile Submenu */}
                    {item.submenu && mobileOpenSubmenu === item.name && (
                      <div className="ml-4 space-y-1 animate-fade-in bg-black/10 py-2 rounded-lg mr-2">
                        {item.submenu.map((subitem) => (
                          <Link
                            key={subitem.name}
                            to={subitem.path}
                            className="block text-white/90 py-2 px-4 hover:bg-white/10 hover:text-white transition text-sm"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subitem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Bottom Separator */}
              <div className="border-t border-white/20 mt-4"></div>

              {/* Social Icons */}
              <div className="flex items-center justify-center gap-4 px-4 py-4">
                <a href="https://www.facebook.com/erimlibelediye/?locale=tr_TR" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/70 hover:text-white transition">
                  <Facebook size={20} aria-hidden="true" />
                </a>
                <a href="https://x.com/erimlibelediyesi" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="text-white/70 hover:text-white transition">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                    <path d="M18.901 3H22.5L14.477 12.026L23.75 24H16.526L10.743 16.596L4.125 24H0.526L9.049 14.373L0.053 3H7.476L12.756 9.946L18.901 3ZM17.653 21.829H19.645L6.426 5.021H4.29L17.653 21.829Z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/erimli.belediyesi/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/70 hover:text-white transition">
                  <Instagram size={20} aria-hidden="true" />
                </a>
                <a href="mailto:info@erimli.bel.tr" aria-label="E-posta gönder" className="text-white/70 hover:text-white transition">
                  <Mail size={20} aria-hidden="true" />
                </a>
                <a href="tel:+904247814003" aria-label="Telefon ile ara" className="text-white/70 hover:text-white transition">
                  <Phone size={20} aria-hidden="true" />
                </a>
                <Link to="/duyurular" aria-label="Duyurular" className="text-white/70 hover:text-white transition" onClick={() => setIsMenuOpen(false)}>
                  <Megaphone size={20} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default Header;
